// src/packages/care/prediction/adapters/calendarCycleModel.ts
//
// The baseline model: a naive arithmetic-average calendar estimate — the same "average recent
// cycle length" heuristic the app uses today, repackaged behind the model interface and using
// the shared ASRM projection. Its point estimate is the Phase-4 baseline the Bayesian model is
// measured against, and it is the fallback for the sparse (1-cycle) tier.
//
// It differs from the Bayesian model ONLY in estimation: a plain mean with a frequentist
// standard-error, no shrinkage/partial-pooling. Confidence is capped (never "high") because it
// does not quantify uncertainty the way the personalized model does.

import { getPeriodStartDates } from "../evidenceMapper";
import { getObservedCycleLengths, mean } from "../cycleStats";
import { projectFertileWindow } from "../cycleProjection";
import {
  CYCLE_LENGTH,
  REGULARITY,
  DATA_SUFFICIENCY,
  KNOWLEDGE_BASE_VERSION,
} from "../knowledgeBase";
import type { FertileWindowModel } from "./types";
import type {
  FertileWindowPrediction,
  PredictionConfidence,
  PredictionWarning,
} from "../fertileWindow.types";

const MODEL_ID = "calendar-baseline";
const MODEL_VERSION = "1.0.0";
/** Provenance stamp: model + the knowledge-base version whose constants it consumes. */
const VERSION_STAMP = `${MODEL_VERSION}+kb.${KNOWLEDGE_BASE_VERSION}`;
/**
 * Floor for the naive within-woman variance (days²). Onset can't be predicted to sub-day
 * precision, so a run of identical logged cycles must not collapse the interval to zero width.
 */
const MIN_WITHIN_VAR = 1;

export const calendarCycleModel: FertileWindowModel = {
  id: MODEL_ID,
  version: MODEL_VERSION,
  isEligible(evidence) {
    // Needs at least one observed cycle to average.
    return getObservedCycleLengths(getPeriodStartDates(evidence)).length >= 1;
  },
  predict(evidence, today): FertileWindowPrediction {
    const starts = getPeriodStartDates(evidence);
    const cycleLengths = getObservedCycleLengths(starts);
    const n = cycleLengths.length;

    // Defensive: predict() is only called when isEligible (n ≥ 1), but never divide by zero.
    if (n === 0) {
      return {
        status: "insufficient_data",
        nextPeriod: null,
        ovulation: null,
        fertileDays: [],
        confidence: "low",
        modelId: MODEL_ID,
        modelVersion: VERSION_STAMP,
        evidenceUsed: [
          {
            type: "period_start",
            observationCount: starts.length,
            status: "ignored",
          },
        ],
        warnings: [],
      };
    }

    const cycleMeanDays = mean(cycleLengths);
    // Naive within-woman variance: sample variance if we have ≥2 cycles, else the population prior.
    const sampleWithinVar =
      n >= 2
        ? cycleLengths.reduce((acc, x) => acc + (x - cycleMeanDays) ** 2, 0) /
          (n - 1)
        : CYCLE_LENGTH.withinWomanSdDays ** 2;
    // Floor so identical logged cycles don't collapse the interval to zero width.
    const withinVar = Math.max(sampleWithinVar, MIN_WITHIN_VAR);
    // Frequentist uncertainty in the mean = standard error².
    const cycleMeanVar = withinVar / n;

    const personalized = n >= DATA_SUFFICIENCY.minCyclesForPersonalized;
    const lastStart = starts[starts.length - 1]!;

    const projection = projectFertileWindow(
      { cycleMeanDays, cycleMeanVar, withinVar },
      lastStart,
      today,
      personalized,
    );

    const range =
      n >= 2 ? Math.max(...cycleLengths) - Math.min(...cycleLengths) : 0;
    const irregular = range > REGULARITY.irregularRangeThresholdDays;

    // Naive baseline never reports "high" confidence.
    const confidence: PredictionConfidence =
      personalized && !irregular ? "medium" : "low";

    const warnings: PredictionWarning[] = [];
    if (!personalized) {
      warnings.push("limited_history");
    }
    if (irregular) {
      warnings.push("irregular_history");
    }

    return {
      status: personalized ? "available" : "partial",
      nextPeriod: projection.nextPeriod,
      ovulation: projection.ovulation,
      fertileDays: projection.fertileDays,
      confidence,
      modelId: MODEL_ID,
      modelVersion: VERSION_STAMP,
      evidenceUsed: [
        {
          type: "period_start",
          observationCount: starts.length,
          status: "applied",
          note: `arithmetic average of ${n} cycle${n === 1 ? "" : "s"}`,
        },
      ],
      warnings,
    };
  },
};
